import React, { useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Tooltip,
  Typography,
  Zoom,
} from "@mui/material";
import TableApp from "../../components/common/tableapp/TableApp";
import { HEADER_HEIGHT } from "../../utils/ui.constants";
import Tablepagination from "../../components/common/tableapp/TablePagination";
import { ListType } from "../../types/list.type";
import { asyncGetList } from "../../utils/httpRequests";
import { useSelector } from "react-redux";
import { LuFilter, LuPlusCircle, LuX } from "react-icons/lu";
import Search from "./components/Search";
import Options from "./components/Options";
import { useAlertContext } from "../../contexts/alert/AlertProvider";
import Actions from "./components/Actions";
import { TableColumn } from "react-data-table-component";

interface IListBase {
  category: ListType;
  defaultCondition?: { [key: string]: any };
  hideTop?: boolean
  hideFilter?: boolean
}

const heights = {
  header: HEADER_HEIGHT,
  py: "32px",
  topList: "50px",
  pagination: "42px",
};
const widthes = {
  filter: "350px",
};

function ListBase({ category, defaultCondition, hideTop, hideFilter }: IListBase) {
  const { showAlert } = useAlertContext();
  const token = useSelector((state: any) => state.auth.token);
  const [expandFilter, setExpandFilter] = useState<boolean>(false);
  const [data, setData] = useState<any[]>([]);
  const [load, setLoad] = useState<number>(0);
  const [fastFields, setFastFields] = useState<
    { label: string; value: string }[]
  >([]);
  const [fastTags, setFastTags] = useState<string[]>([]);
  const [fastCondition, setFastCondition] = useState<any[]>([]);
  const [filter, setFilter] = useState<{ [key: string]: any }>({});
  const [filterMode, setFilterMode] = useState<number>(1); // 1: kết hợp; 2: Riêng lẻ
  const [loadFilter, setLoadFilter] = useState<number>(0);
  const [condition, setCondition] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openModalForm, setOpenModalForm] = useState<boolean>(false);
  const [clickedData, setClickedData] = useState<any>();
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [isEdit, setIsEdit] = useState<boolean>(false);
  const [toggleClear, setToggleClear] = useState<boolean>(false);
  const [paginationOption, setPaginationOption] = useState<{
    page: number;
    limit: number;
    totalRows: number;
  }>({
    limit: 20,
    page: 1,
    totalRows: 0,
  });

  const applyFilter = () => {
    setFastTags([]);
    setFastCondition([]);
    const conditionFilter = category?.generateCondition?.(filter);
    setCondition(conditionFilter || []);
  };

  const refreshFilter = () => {
    setLoadFilter(loadFilter + 1);
  };

  const applyFastText = (fastTags: string[]) => {
    if (fastTags?.length > 0) {
      const fastCondition: any[] = [];
      for (let i = 0; i < fastTags.length; i++) {
        const word = fastTags[i];
        const block = {
          $or: (fastFields || []).map((item) => {
            return { [item.value]: { $regex: word, $options: "i" } };
          }),
        };
        fastCondition.push(block);
      }
      setFastCondition(fastCondition);
    } else {
      setFastCondition([]);
      setLoad(load + 1);
    }
    refreshFilter();
    setCondition([]);
    setExpandFilter(false);
  };

  const handleRowClicked = (row: any) => {
    if (category.Form) {
      setClickedData(row);
      setOpenModalForm(true);
      setIsEdit(true);
    }
  };

  const handleCloseModalForm = () => {
    setClickedData(null);
    setOpenModalForm(false);
    setIsEdit(false);
  };

  // get products
  const getListData = async () => {
    try {
      if (loading) return;
      setLoading(true);
      let query: any = {};
      if (fastCondition.length > 0) {
        query.$and = fastCondition;
      } else {
        if (filterMode === 1) {
          query.$and = condition;
        } else {
          query.$or = condition;
        }
      }
      const resp = await asyncGetList({
        apiCode: category.apiCode,
        withIdApp: true,
        applyToken: true,
        token,
        condition: {
          limit: paginationOption.limit,
          page: paginationOption.page,
          q: { ...defaultCondition, ...query },
        },
      });
      const respCount = await asyncGetList({
        apiCode: category.apiCode,
        withIdApp: true,
        applyToken: true,
        token,
        condition: {
          count: 1,
          q: { ...defaultCondition, ...query },
        },
      });
      if (respCount.status === 200) {
        setPaginationOption({
          ...paginationOption,
          totalRows: respCount.data.rows_number || 0,
        });
      }
      if (resp.status === 200) {
        setData(resp.data || []);
        setLoading(false);
      }
    } catch (error: any) {
      showAlert({
        type: "error",
        message:
          error?.data?.message || error?.data?.error || "Lỗi khi tải dữ liệu",
      });
    } finally {
      setToggleClear(!toggleClear);
      setSelectedRows([]);
    }
  };

  // row per page change
  const handleRowPerPageChange = (value: number) => {
    setPaginationOption({ ...paginationOption, page: 1, limit: value });
  };

  const columns: TableColumn<any>[] = useMemo(() => {
    return (category.columns || []).map((col: TableColumn<any>) => {
      if (!col.cell) {
        return {
          ...col,
          cell: (row, index) => {
            return (
              <Tooltip
                placement="right"
                arrow
                TransitionComponent={Zoom}
                title={`${
                  col.format ? col.format(row, index) : col.selector?.(row)
                }`}
              >
                <div data-tag="allowRowEvents" className="two-lines-ellipsis">
                  {col.format ? col.format(row, index) : col.selector?.(row)}
                </div>
              </Tooltip>
            );
          },
        };
      }
      return col;
    });
  }, [category.columns]);

  useEffect(() => {
    if (category.apiCode) {
      getListData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationOption.limit, paginationOption.page, filterMode, load]);

  useEffect(() => {
    if (category.apiCode) {
      setCondition([]);
      setFastCondition([]);
      setFastTags([]);
      setPaginationOption({ limit: 20, page: 1, totalRows: 0 });
      setFastFields(category.fastFields || []);
      setLoad(load + 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category.apiCode]);
  useEffect(() => {
    setPaginationOption({ limit: 20, page: 1, totalRows: 0 });
    setLoad(load + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  return (
    <>
      {/* Form  */}
      {category?.Form && openModalForm && (
        <category.Form
          apiCode={category?.apiCode}
          isEdit={isEdit}
          defaultValues={clickedData}
          open={openModalForm}
          onClose={handleCloseModalForm}
          setLoad={setLoad}
        />
      )}
      {/* Form end */}
      <Box
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          backgroundColor: "background.paper",
          borderRadius: "10px",
        }}
      >
        {/* Top */}
        {!hideTop && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            flexShrink: 0,
            height: heights.topList,
            px: 2,
            borderBottom: "1px dashed",
            borderColor: "divider",
          }}
        >
          <Typography
            sx={{ fontSize: "20px", lineHeight: "20px", fontWeight: 600 }}
          >
            {category?.title || "Danh sách"}
          </Typography>
          {fastFields && fastFields.length > 0 && (
            <Box sx={{ width: "35%" }}>
              <Search
                loading={loading}
                tags={fastTags}
                setTags={setFastTags}
                originFields={category.fastFields || []}
                fields={fastFields}
                setFields={setFastFields}
                handleSearch={applyFastText}
              />
            </Box>
          )}
          <Stack direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                visibility: selectedRows?.length > 0 ? "visible" : "hidden",
              }}
            >
              <Actions
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                setToggleClear={setToggleClear}
                apiCode={category?.apiCode}
                setLoad={setLoad}
              />
            </Box>
            {category?.allowCreate && (
              <Button
                variant="contained"
                color="primary"
                size="small"
                sx={{
                  boxShadow: "none",
                  color: "common.white",
                  textTransform: "none",
                  "&:hover": { boxShadow: "none" },
                }}
                startIcon={<LuPlusCircle size={16} />}
                onClick={() => setOpenModalForm(true)}
              >
                Thêm
              </Button>
            )}
            <Options category={category} setLoad={setLoad} />
            <Badge
              badgeContent={condition.length || 0}
              sx={{
                "& .MuiBadge-badge": {
                  backgroundColor: "error.main",
                  color: "common.white",
                  border: "1px solid",
                  borderColor: "common.white",
                  top: "5px",
                  right: "2px",
                },
              }}
            >
              <IconButton
                onClick={() => setExpandFilter(!expandFilter)}
                sx={{
                  borderRadius: "10px",
                  color: "common.white",
                  backgroundColor: "secondary.main",
                  "&:hover": {
                    backgroundColor: "secondary.main",
                  },
                }}
              >
                <LuFilter size={16} />
              </IconButton>
            </Badge>
          </Stack>
        </Stack>
        )}
        {/* Top end */}
        {/* Table */}
        <Box
          id="table-filter-layout"
          sx={{
            width: "100%",
            height: `calc(100vh - ${heights.header} - ${heights.py} - ${heights.topList} - ${heights.pagination})`,
            display: "flex",
            flexDirection: "row",
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              width: `calc(100%${expandFilter ? ` - ${widthes.filter}` : ""})`,
              height: "100%",
              transition: "all 0.1s",
              transitionTimingFunction: "ease-out",
            }}
          >
            <TableApp
              toggleCleared={toggleClear}
              rowPerPage={paginationOption.limit}
              columns={columns}
              data={data}
              selectableRows
              fixedHeader
              progressPending={loading}
              onRowClicked={handleRowClicked}
              onSelectedRowsChange={setSelectedRows}
              fixedHeaderScrollHeight={`calc(100vh - ${HEADER_HEIGHT} - ${heights.py} - ${heights.pagination} - ${heights.topList})`}
            />
          </Box>
          {/* Filter */}
          <Box
            sx={{
              height: "100%",
              width: expandFilter ? widthes.filter : "0px",
              transition: "all 0.1s",
              transitionTimingFunction: "ease-out",
              borderLeft: "2px solid",
              borderColor: expandFilter ? "primary.main" : "transparent",
              overflow: "hidden",
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{
                height: "40px",
                px: 2,
                borderBottom: "1px solid",
                borderColor: "divider",
              }}
            >
              <Stack direction="row" alignItems="center" gap={1}>
                <IconButton onClick={() => setExpandFilter(false)}>
                  <LuX size={20} />
                </IconButton>
                <Typography sx={{ fontWeight: 600, color: "secondary.main" }}>
                  Chế độ lọc
                </Typography>
              </Stack>
              <Box sx={{ width: "120px" }}>
                <Select
                  fullWidth
                  value={filterMode}
                  onChange={(e) => {
                    setFilterMode(e.target.value as number);
                  }}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    "& .MuiSelect-select": {
                      px: 1,
                      py: 0.5,
                    },
                    "& fieldset": {
                      border: "none",
                    },
                  }}
                >
                  <MenuItem value={1}>Kết hợp</MenuItem>
                  <MenuItem value={2}>Tách</MenuItem>
                </Select>
              </Box>
            </Stack>
            <Box
              sx={{
                width: "100%",
                height: "calc(100% - 40px - 45px)",
                overflow: "auto",
                px: 2,
                py: 1,
              }}
            >
              {category?.Filter && (
                <category.Filter
                  filter={filter}
                  setFilter={setFilter}
                  load={loadFilter}
                />
              )}
            </Box>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="center"
              gap={1}
              sx={{
                height: "45px",
                borderTop: "1px dashed",
                borderColor: "divider",
              }}
            >
              <Button
                onClick={applyFilter}
                variant="contained"
                size="small"
                sx={{
                  textTransform: "none",
                  color: "common.white",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Lọc
              </Button>
              <Button
                onClick={refreshFilter}
                variant="contained"
                size="small"
                color="info"
                sx={{
                  textTransform: "none",
                  color: "common.white",
                  boxShadow: "none",
                  "&:hover": { boxShadow: "none" },
                }}
              >
                Làm mới
              </Button>
            </Stack>
          </Box>
        </Box>
        {/* Table end */}
        {/* Pagination */}
        <Box
          sx={{
            mt: "auto",
            borderTop: "1px dashed",
            borderColor: "divider",
            height: "42px",
          }}
        >
          {paginationOption.totalRows > 0 && (
            <Tablepagination
              currentPage={paginationOption.page}
              onChangePage={(value) =>
                setPaginationOption({ ...paginationOption, page: value })
              }
              rowsPerPage={paginationOption.limit}
              rowCount={paginationOption.totalRows}
              onChangeRowsPerPage={handleRowPerPageChange}
              paginationRowsPerPageOptions={[20, 50, 100, 500]}
            />
          )}
        </Box>
        {/* Pagination end */}
      </Box>
    </>
  );
}

export default ListBase;
